import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import toast from "react-hot-toast";
import Icon from "../../../../components/AppIcon";
import Button from "../../../../components/ui/Button";

const API = import.meta.env.VITE_API_BASE_URL;

// YYYY-MM-DD for <input type="date" />
const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const safeArray = (x) => (Array.isArray(x) ? x : []);

const slotLabel = (s) => {
  const name = s?.name ? `${s.name} • ` : "";
  const st = s?.startTime || "";
  const et = s?.endTime || "";
  if (st && et) return `${name}${st} - ${et}`;
  return name || `Slot #${s?.id}`;
};

export default function RescheduleOrderModal({
  isOpen,
  onClose,
  order,
  onSuccess,
}) {
  const isHome = !!order?.isHomeSample;

  // ✅ must exist when center visit
  const centerId = order?.centerId;

  // ✅ you must decide which category to use for center slots
  // we try: order.categoryId -> order.centerSlot.categoryId -> fallback null
  const initialCategoryId =
    order?.categoryId ||
    order?.centerSlot?.categoryId ||
    order?.testTypeCategoryId || // optional if you have
    null;

  const [date, setDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [categoryId, setCategoryId] = useState(initialCategoryId ? String(initialCategoryId) : "");

  // dropdown selection
  const [selectedSlotId, setSelectedSlotId] = useState("");

  // fetched slots
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // categories for dropdown
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setDate(toDateInput(order?.date || order?.createdAt));
    setRemarks(order?.remarks || "");

    // preload selection from existing order
    if (isHome) setSelectedSlotId(order?.slotId ? String(order.slotId) : "");
    else setSelectedSlotId(order?.centerSlotId ? String(order.centerSlotId) : "");

    // init category for center reschedule
    const cid =
      order?.categoryId ||
      order?.centerSlot?.categoryId ||
      initialCategoryId ||
      null;

    setCategoryId(cid ? String(cid) : "");

    // fetch categories for the dropdown
    if (!isHome) {
      (async () => {
        try {
          setCategoriesLoading(true);
          const res = await axios.get(`${API}/api/categories`);
          const list = Array.isArray(res.data) ? res.data : res.data?.data || res.data?.categories || [];
          setCategories(list);
        } catch (err) {
          console.error("fetchCategories error:", err);
          setCategories([]);
        } finally {
          setCategoriesLoading(false);
        }
      })();
    }
  }, [isOpen, order, isHome]);

  const canFetchCenterSlots = useMemo(() => {
    if (isHome) return true;
    // center flow requires centerId and categoryId
    return Boolean(centerId) && Boolean(categoryId);
  }, [isHome, centerId, categoryId]);

  const normalizeSlots = (resData) => {
    // support common shapes:
    // { slots: [...] } OR { data: [...] } OR [...] OR { slots: {slots:[...]}}
    if (Array.isArray(resData)) return resData;
    if (Array.isArray(resData?.slots)) return resData.slots;
    if (Array.isArray(resData?.data)) return resData.data;
    if (Array.isArray(resData?.slots?.slots)) return resData.slots.slots;
    return [];
  };

  const fetchSlots = async (dateStr) => {
    if (!dateStr) return;

    // Home sample does not need categoryId/centerId
    if (!isHome && !canFetchCenterSlots) {
      setSlots([]);
      setSelectedSlotId("");
      return;
    }

    try {
      setSlotsLoading(true);

      let url = "";
      let params = { date: dateStr };

      if (isHome) {
        // ✅ your endpoint
        url = `${API}/api/slots/date`;
      } else {
        // ✅ your endpoint
        url = `${API}/api/centers/${centerId}/slots`;
        params = { ...params, categoryId: Number(categoryId) };
      }

      const res = await axios.get(url, { params });

      const list = normalizeSlots(res.data);
      setSlots(safeArray(list));

      // if currently selected is not in list, reset
      const exists = safeArray(list).some((s) => String(s.id) === String(selectedSlotId));
      if (!exists) setSelectedSlotId("");
    } catch (e) {
      console.error("fetchSlots error:", e);
      toast.error(e?.response?.data?.message || "Failed to fetch slots");
      setSlots([]);
      setSelectedSlotId("");
    } finally {
      setSlotsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!date) return;
    fetchSlots(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, date, isHome, centerId, categoryId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!date) return toast.error("Please select date");
    if (!selectedSlotId) return toast.error("Please select a slot");
    if (!isHome && !categoryId) return toast.error("Please select category");

    try {
      const payload = {
        date: new Date(date).toISOString(),
        remarks: remarks || null,

        // ✅ RULE:
        slotId: isHome ? Number(selectedSlotId) : null,
        centerSlotId: !isHome ? Number(selectedSlotId) : null,

        // optional (if your backend wants it)
        // categoryId: !isHome ? Number(categoryId) : null,
      };

      await axios.put(`${API}/api/orders/${order.id}/reschedule`, payload, { withCredentials: true });

      toast.success("Order rescheduled");
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("reschedule error:", err);
      toast.error(err?.response?.data?.message || "Failed to reschedule");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md shadow-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Reschedule Order</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isHome ? "Home Sample Slot" : "Center Slot"}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <Icon name="X" size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-3 space-y-3">
          {/* Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-2 py-2 border border-gray-300 rounded text-xs"
              required
            />
          </div>

          {/* Category (ONLY for center visit) */}
          {!isHome && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded text-xs bg-white"
                disabled={categoriesLoading}
                required
              >
                <option value="">
                  {categoriesLoading ? "Loading categories..." : "Select a category"}
                </option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Slots Dropdown */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Select Slot
            </label>

            {!isHome && (!centerId || !categoryId) ? (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                {(!centerId && "Center not assigned to this order.") ||
                  (!categoryId && "CategoryId required to fetch center slots.")}
              </div>
            ) : (
              <>
                <select
                  value={selectedSlotId}
                  onChange={(e) => setSelectedSlotId(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded text-xs bg-white"
                  disabled={slotsLoading}
                  required
                >
                  <option value="">
                    {slotsLoading ? "Loading slots..." : "Select a slot"}
                  </option>

                  {slots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {slotLabel(s)}
                    </option>
                  ))}
                </select>

                {!slotsLoading && slots.length === 0 && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    No slots available for selected date.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Remarks (optional)
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows="2"
              className="w-full px-2 py-2 border border-gray-300 rounded text-xs"
              placeholder="Reason for reschedule..."
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="text-xs">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="solid"
              size="sm"
              className="text-xs bg-blue-600 hover:bg-blue-700 text-white"
              disabled={slotsLoading || !selectedSlotId || !date || (!isHome && !canFetchCenterSlots)}
            >
              <Icon name="CalendarCheck" size={14} className="mr-1" />
              Reschedule
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
